// Main scene vertex shader
export const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_uv;

  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

// Blur vertex shader (same as main)
export const blurVertexShaderSource = vertexShaderSource

export const fragmentShaderSource = `
  precision mediump float;

  uniform vec2 u_resolution;
  uniform float u_time;
  uniform vec2 u_sunCenter;  // Sun center in NDC coordinates
  uniform float u_sunRadius; // Sun radius in NDC coordinates
  varying vec2 v_uv;

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution;

    // Define horizon line at 1/3 from bottom
    float horizonY = 1.0 / 3.0;

    // Colors from logo
    vec3 peachColor = vec3(0.918, 0.545, 0.451); // #EA8B73
    vec3 purpleColor = vec3(0.286, 0.129, 0.431); // #49216E
    vec3 sunColor = vec3(0.961, 0.820, 0.302); // #F5D14D

    // Convert fragment position from screen space [0,1] to NDC [-1,1]
    vec2 ndcPos = v_uv * 2.0 - 1.0;

    // Calculate distance from sun center (with aspect ratio correction)
    float aspectRatio = u_resolution.x / u_resolution.y;
    vec2 adjustedPos = vec2(ndcPos.x * aspectRatio, ndcPos.y);
    vec2 adjustedSunCenter = vec2(u_sunCenter.x * aspectRatio, u_sunCenter.y);
    float distToSun = distance(adjustedPos, adjustedSunCenter);

    // Start with sky or ocean based on height
    vec3 color;
    if (uv.y > horizonY) {
      // Sky (top 2/3) - peach color
      color = peachColor;
    } else {
      // Ocean (bottom 1/3) - purple color
      color = purpleColor;
    }

    // Draw sun (only the part above horizon, since ocean will cover the bottom)
    // Sun is in front of sky but behind ocean
    if (uv.y > horizonY && distToSun < u_sunRadius) {
      // We're in the sun area and above the horizon
      color = sunColor;
    }

    gl_FragColor = vec4(color, 1.0);
  }
`

// Separable Gaussian blur fragment shader
export const blurFragmentShaderSource = `
  precision mediump float;

  uniform sampler2D u_texture;
  uniform vec2 u_resolution;
  uniform vec2 u_direction; // vec2(1, 0) for horizontal, vec2(0, 1) for vertical
  varying vec2 v_uv;

  void main() {
    vec2 texelSize = 1.0 / u_resolution;

    // 5-tap Gaussian blur weights (reduced radius for subtler blur)
    // Sigma ≈ 1.0 for gentle edge smoothing
    vec3 result = texture2D(u_texture, v_uv).rgb * 0.398942;

    // Sample in both directions along the blur axis
    vec2 offset1 = u_direction * texelSize;
    result += texture2D(u_texture, v_uv + offset1).rgb * 0.241971;
    result += texture2D(u_texture, v_uv - offset1).rgb * 0.241971;

    vec2 offset2 = u_direction * texelSize * 2.0;
    result += texture2D(u_texture, v_uv + offset2).rgb * 0.054058;
    result += texture2D(u_texture, v_uv - offset2).rgb * 0.054058;

    gl_FragColor = vec4(result, 1.0);
  }
`

// Grid/coordinate system vertex shader
export const gridVertexShaderSource = `
  attribute vec2 a_position;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

// Grid/coordinate system fragment shader
export const gridFragmentShaderSource = `
  precision mediump float;

  void main() {
    gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0); // White tick marks
  }
`

// Text label vertex shader
export const textVertexShaderSource = `
  attribute vec2 a_position;
  attribute vec2 a_texCoord;
  varying vec2 v_texCoord;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`

// Text label fragment shader
export const textFragmentShaderSource = `
  precision mediump float;

  uniform sampler2D u_texture;
  varying vec2 v_texCoord;

  void main() {
    vec4 texColor = texture2D(u_texture, v_texCoord);
    gl_FragColor = texColor;
  }
`

// Sun circle vertex shader
export const sunCircleVertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_position;

  void main() {
    v_position = a_position;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

// Sun circle fragment shader (for dashed/dotted circles)
export const sunCircleFragmentShaderSource = `
  precision mediump float;

  uniform vec2 u_center;        // Center in NDC coordinates
  uniform float u_radius;       // Radius in NDC coordinates
  uniform float u_lineWidth;    // Line width in NDC coordinates
  uniform int u_dashType;       // 0 = dashed, 1 = dotted
  uniform vec2 u_resolution;    // Screen resolution for aspect correction

  varying vec2 v_position;

  void main() {
    // Apply aspect ratio correction
    float aspectRatio = u_resolution.x / u_resolution.y;
    vec2 adjustedPos = vec2(v_position.x * aspectRatio, v_position.y);
    vec2 adjustedCenter = vec2(u_center.x * aspectRatio, u_center.y);

    // Calculate distance from center
    float dist = distance(adjustedPos, adjustedCenter);

    // Check if we're on the circle edge (within line width)
    float innerRadius = u_radius - u_lineWidth / 2.0;
    float outerRadius = u_radius + u_lineWidth / 2.0;

    if (dist < innerRadius || dist > outerRadius) {
      discard;
    }

    // Calculate angle around the circle
    vec2 toPoint = adjustedPos - adjustedCenter;
    float angle = atan(toPoint.y, toPoint.x);

    // Create dashed or dotted pattern
    float pattern;
    if (u_dashType == 0) {
      // Dashed: 12 dashes around the circle
      float dashCount = 12.0;
      float angleNormalized = (angle + 3.14159265) / (2.0 * 3.14159265); // 0 to 1
      pattern = mod(angleNormalized * dashCount, 1.0);
      if (pattern > 0.5) {
        discard;
      }
    } else {
      // Dotted: 24 dots around the circle
      float dotCount = 24.0;
      float angleNormalized = (angle + 3.14159265) / (2.0 * 3.14159265); // 0 to 1
      pattern = mod(angleNormalized * dotCount, 1.0);
      if (pattern > 0.3) {
        discard;
      }
    }

    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); // Black
  }
`
